import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '@/store/gameStore';
import { CharacterPortrait } from '@/components/game/CharacterPortrait';
import { StatBadge } from '@/components/game/StatBadge';
import { VsBadge } from '@/components/game/VsBadge';
import { TimerDisplay } from '@/components/game/TimerDisplay';
import { Button } from '@/components/ui/button';
import { duckMusic, isMusicMuted, playMusic, playSfx, restoreMusicVolume, setMusicMuted, setMusicVolume, stopMusic } from '@/lib/audio';
import { toast } from 'sonner';
import { ChevronsUp, ShoppingBag, TrendingDown, TrendingUp, Volume2, VolumeX, type LucideIcon } from 'lucide-react';
import { getCharacterStatValue, getNextBattleStat, STAT_LABELS, getRandomStat } from '@/data/characters';
import {
  subscribeToMatch,
  subscribeToPlayers,
  subscribeToBattleRounds,
  updatePlayerSelection,
  purchaseBoost,
  forfeitMatch,
} from '@/lib/multiplayer';
import {
  submitCharacterSelectionV2,
  resolveBattleRoundV2,
  quickLockSelectionV2,
} from '@/lib/multiplayer-v2';
import { getSupabaseClient } from '@/lib/supabase';

type BattlePhase = 'stat-reveal' | 'selection' | 'comparison' | 'result';

// How long the result card stays up before auto-advancing in PvP.
const RESULT_AUTOADVANCE_MS = 2000;
// How long the "Next Round" CPU button is disabled (prevents mis-taps).
const RESULT_HOLD_MS = 1500;

const QUICK_MESSAGES = [
  { label: '😂 LMAO',           text: '😂 LMAO' },
  { label: '😤 EZ',             text: '😤 EZ' },
  { label: '🎉 YESSS!',         text: '🎉 YESSS!' },
  { label: '😭 NOOO!',          text: '😭 NOOO!' },
  { label: '😮 No way',         text: '😮 No way' },
  { label: '⏩ HURRY UP',       text: '⏩ HURRY UP' },
  { label: '🙏 Please',         text: '🙏 Please' },
  { label: '😂 haha',           text: '😂 haha' },
  { label: '😐 Seriously?',     text: '😐 Seriously?' },
  { label: "🔥 YOU'RE COOKED",  text: "🔥 YOU'RE COOKED" },
  { label: "💀 I'M COOKED",     text: "💀 I'M COOKED" },
  { label: '💸 BIG SPEND',      text: '💸 BIG SPEND' },
  { label: '🪙 LOW SPEND',      text: '🪙 LOW SPEND' },
  { label: '🔒 LOCK IN',        text: '🔒 LOCK IN' },
  { label: '😮 WOW',            text: '😮 WOW' },
];

export function BattleScreen() {
  const {
    matchId,
    playerId,
    playerRole,
    player,
    opponent,
    currentStat,
    currentBattleRound,
    battleHistory,
    settings,
    timeRemaining,
    setTimeRemaining,
    playerBoosts,
    opponentBoosts,
    purchaseReroll,
    purchaseDoubleWin,
    purchaseExtraTime,
    purchaseStatBoost,
    purchaseWeaken,
    selectCharacter,
    lockSelection,
    lockOpponentSelection,
    resolveBattle,
    nextBattleRound,
    mode,
    applyMatchSnapshot,
    applyPlayersSnapshot,
    applyBattleHistorySnapshot,
    setPhase,
    setResultOverride,
    resetGame,
    useV2,
    stateVersion,
    setStateVersion,
    cpuApplyOpponentBoost,
    tiredPlayerId: storeTiredPlayerId,
    tiredOpponentId: storeTiredOpponentId,
  } = useGameStore();

  const [phase, setBattlePhase] = useState<BattlePhase>('stat-reveal');
  const [boostBanner, setBoostBanner] = useState<string | null>(null);
  const [opponentBoostBanner, setOpponentBoostBanner] = useState<string | null>(null);
  const [opponentBoostAlert, setOpponentBoostAlert] = useState<{ icon: LucideIcon; label: string; sub: string } | null>(null);
  const [shopOpen, setShopOpen] = useState(false);
  const [resultHold, setResultHold] = useState(false);
  const [musicMuted, setMusicMutedState] = useState(() => isMusicMuted());
  // Tired character IDs persisted in state so the UI re-renders immediately
  const [tiredPlayerIdState, setTiredPlayerIdState] = useState<string | null>(null);
  const [tiredOpponentIdState, setTiredOpponentIdState] = useState<string | null>(null);
  // (playerBoosts.statBoostPct is reset to 0 after each round resolves — ResultsScreen uses statBoostPctRecord instead)

  const phaseRef = useRef<BattlePhase>('stat-reveal');
  const isResolving = useRef(false);
  // Tracks battleHistory.length when comparison phase started — used by the guest
  // to detect when the new round result has arrived before rendering the result card.
  const battleHistoryLenAtComparisonRef = useRef(0);
  const isHost = playerRole === 'host';
  const opponentLeftRef = useRef(false);
  const playersCountRef = useRef(0);
  const lastHeartbeat = useRef<number | null>(null);
  const lastCrowdRef = useRef<string | null>(null);
  const cpuTimerRef = useRef<number | null>(null);
  const cpuTimeRef = useRef(15);
  // Tracks when the selection phase started (wall-clock) for tab-return resync
  const selectionStartedAtRef = useRef<number | null>(null);
  // Quick messages
  const [incomingMessage, setIncomingMessage] = useState<string | null>(null);
  const [sentMessage, setSentMessage] = useState<string | null>(null);
  const [messageCooldown, setMessageCooldown] = useState(false);
  const messageCooldownTimerRef = useRef<number | null>(null);
  const incomingTimerRef = useRef<number | null>(null);
  const quickMsgChannelRef = useRef<{ send: (msg: object) => Promise<unknown> } | null>(null);

  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // ─── REROLL SYNC: restart timer when stat changes mid-selection ──────────
  // Handles the case where the *opponent* bought a reroll — the stat changes
  // via applyMatchSnapshot, so we need to unlock and restart the timer here.
  const prevStatRef = useRef<typeof currentStat>(currentStat);
  useEffect(() => {
    if (mode !== 'pvp') return;
    if (currentStat === prevStatRef.current) { prevStatRef.current = currentStat; return; }
    prevStatRef.current = currentStat;
    if (phaseRef.current !== 'selection') return;
    // Stat changed during selection — unlock and restart timer
    cpuTimeRef.current = 15;
    setTimeRemaining(15);
    selectionStartedAtRef.current = Date.now();
    if (cpuTimerRef.current) { clearInterval(cpuTimerRef.current); cpuTimerRef.current = null; }
    useGameStore.setState((s) => ({
      player: { ...s.player, selectionLocked: false, selectedCharacter: null },
      lastLocalSelectionLockAt: null,
    }));
    cpuTimerRef.current = window.setInterval(() => {
      cpuTimeRef.current = Math.max(0, cpuTimeRef.current - 1);
      setTimeRemaining(cpuTimeRef.current);
      if (cpuTimeRef.current <= 5 && cpuTimeRef.current > 0) {
        setMusicVolume(0.9);
        if (!momentumActive && lastHeartbeat.current !== cpuTimeRef.current) {
          playSfx('heartbeat_tick', 0.4);
          lastHeartbeat.current = cpuTimeRef.current;
        }
      } else if (cpuTimeRef.current > 5) {
        restoreMusicVolume();
        lastHeartbeat.current = null;
      }
      if (cpuTimeRef.current <= 0) {
        clearInterval(cpuTimerRef.current!);
        cpuTimerRef.current = null;
        const latestPlayer = useGameStore.getState().player;
        if (!latestPlayer.selectionLocked && latestPlayer.team.length > 0) {
          const avail = latestPlayer.team.filter((c) => c.id !== tiredPlayerIdRef.current);
          const pool = avail.length ? avail : latestPlayer.team;
          const picked = pool[Math.floor(Math.random() * pool.length)];
          if (!picked) return;
          selectCharacter(picked);
          lockSelection();
          if (useV2) void submitCharacterSelectionV2(matchId!, picked.id);
          else void updatePlayerSelection(matchId!, picked.id, true, playerId);
        }
      }
    }, 1000);
  }, [currentStat]);

  // ─── TAB VISIBILITY: re-sync display on return ──────────────────────────
  // Timer keeps counting even when tab is hidden — both players stay in sync.
  // On tab return we recalculate remaining time from when the phase started.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && phaseRef.current === 'selection' && selectionStartedAtRef.current !== null) {
        const elapsed = Math.floor((Date.now() - selectionStartedAtRef.current) / 1000);
        const remaining = Math.max(0, 15 - elapsed);
        cpuTimeRef.current = remaining;
        setTimeRemaining(remaining);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const lastRound = battleHistory[battleHistory.length - 1];

  // Track stat that was active during the result phase so stat-reveal can
  // detect when the server has delivered a genuinely new stat for the next round.
  const resultStatRef = useRef<typeof currentStat>(null);
  useEffect(() => {
    if (phase === 'result') resultStatRef.current = currentStat;
  }, [phase, currentStat]);

  // ─── TIRED CHARACTER ─────────────────────────────────────────────────────
  // The character used in the previous round must rest this round.
  // We keep tired IDs in BOTH state (triggers re-render for UI) and a ref
  // (readable synchronously in timer callbacks).
  const tiredPlayerIdRef = useRef<string | null>(null);
  const tiredOpponentIdRef = useRef<string | null>(null);

  // Sync state → ref whenever state changes so timer callbacks stay correct
  useEffect(() => { tiredPlayerIdRef.current = tiredPlayerIdState; }, [tiredPlayerIdState]);
  useEffect(() => { tiredOpponentIdRef.current = tiredOpponentIdState; }, [tiredOpponentIdState]);

  // When battleHistory gets a new round appended, update tired state immediately
  const prevHistoryLenRef = useRef(0);
  useEffect(() => {
    const len = battleHistory.length;
    if (len > 0 && len !== prevHistoryLenRef.current) {
      prevHistoryLenRef.current = len;
      const last = battleHistory[len - 1];
      setTiredPlayerIdState(last?.playerCharacter?.id ?? null);
      setTiredOpponentIdState(last?.opponentCharacter?.id ?? null);
    }
    if (len === 0) {
      prevHistoryLenRef.current = 0;
      setTiredPlayerIdState(null);
      setTiredOpponentIdState(null);
    }
  }, [battleHistory]);

  // For PvP: use store-level tiredPlayerId (set by applyBattleHistorySnapshot from server)
  // which is available immediately when battleHistory arrives, avoiding the race condition
  // where the selection phase starts before the local useEffect updates tiredPlayerIdState.
  // For CPU: local state is fine since everything is synchronous.
  const effectiveTiredPlayerId = mode === 'pvp' ? (storeTiredPlayerId ?? tiredPlayerIdState) : tiredPlayerIdState;
  const effectiveTiredOpponentId = mode === 'pvp' ? (storeTiredOpponentId ?? tiredOpponentIdState) : tiredOpponentIdState;

  const playerMatchPoint = player.roundsWon === settings.battleFormat - 1;
  const opponentMatchPoint = opponent.roundsWon === settings.battleFormat - 1;
  const momentumActive = playerMatchPoint || opponentMatchPoint;

  const getDisplayStat = (
    character: typeof player.team[0] | null,
    boostPct: number,
    debuffPct: number = 0
  ) => {
    if (!character || !currentStat) return 0;
    return Math.round(
      getCharacterStatValue(character, currentStat) * (1 + boostPct / 100) * (1 - debuffPct / 100)
    );
  };

  // ─── CPU BOOST AI ─────────────────────────────────────────────────────────
  const applyCpuBoosts = useCallback(() => {
    if (mode !== 'cpu') return;
    const isHard = settings.cpuDifficulty === 'hard';
    const { coins } = opponent;
    const { statBoostUsed, weakenUsed, doubleWinUsed } = opponentBoosts;
    // Situational awareness: use boosts more aggressively when falling behind.
    const isLosing = opponent.roundsWon < player.roundsWon;
    const isWinning = opponent.roundsWon > player.roundsWon;

    if (!statBoostUsed) {
      if (isHard) {
        const chance = isLosing ? 0.85 : isWinning ? 0.50 : 0.70;
        if (coins >= 1000 && Math.random() < chance) cpuApplyOpponentBoost('stat_boost', 10);
        else if (coins >= 500 && Math.random() < chance * 0.65) cpuApplyOpponentBoost('stat_boost', 5);
      } else if (coins >= 500 && Math.random() < 0.28) {
        cpuApplyOpponentBoost('stat_boost', 5);
      }
    }
    if (!weakenUsed) {
      if (isHard) {
        const chance = isLosing ? 0.65 : isWinning ? 0.30 : 0.50;
        if (coins >= 600 && Math.random() < chance) {
          cpuApplyOpponentBoost('weaken', (coins >= 1200 && Math.random() < 0.50 ? 10 : 5) as 5 | 10);
        }
      } else if (coins >= 600 && Math.random() < 0.18) {
        cpuApplyOpponentBoost('weaken', 5);
      }
    }
    if (!doubleWinUsed) {
      if (isHard && coins >= 2000) {
        const chance = isLosing ? 0.55 : isWinning ? 0.15 : 0.32;
        if (Math.random() < chance) cpuApplyOpponentBoost('double_win');
      } else if (!isHard && coins >= 2000 && Math.random() < 0.10) {
        cpuApplyOpponentBoost('double_win');
      }
    }
  }, [mode, settings.cpuDifficulty, opponent, opponentBoosts, cpuApplyOpponentBoost, player.roundsWon]);

  // ─── CPU SELECTION ────────────────────────────────────────────────────────
  const generateCpuSelection = useCallback(() => {
    if (mode !== 'cpu' || !currentStat) return null;
    const available = opponent.team.filter((c) => c.id !== tiredOpponentIdRef.current);
    const pool = available.length ? available : opponent.team;
    if (!pool.length) return null;
    const sorted = [...pool].sort(
      (a, b) => getCharacterStatValue(b, currentStat) - getCharacterStatValue(a, currentStat)
    );
    // Easy: 30% of the time pick a suboptimal character (not the worst, just not the best).
    // This feels more realistic than pure random while still being beatable.
    if (settings.cpuDifficulty === 'easy' && sorted.length > 1 && Math.random() < 0.30) {
      const suboptimal = sorted.slice(1); // everything except the best
      return suboptimal[Math.floor(Math.random() * suboptimal.length)];
    }
    return sorted[0];
  }, [mode, currentStat, opponent.team, settings.cpuDifficulty]);

  // ─── STAT REVEAL → SELECTION ──────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'stat-reveal') return;
    const delay = settings.hiddenStat ? 100 : 2500;
    const t = window.setTimeout(() => {
      selectionStartedAtRef.current = Date.now();
      setBattlePhase('selection');
      cpuTimeRef.current = 15;
      setTimeRemaining(15);
    }, delay);
    return () => window.clearTimeout(t);
  }, [phase, currentBattleRound, settings.hiddenStat]);

  // ─── SELECTION TIMER ──────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'selection') return;
    cpuTimeRef.current = 15;
    setTimeRemaining(15);

    if (cpuTimerRef.current) clearInterval(cpuTimerRef.current);
    cpuTimerRef.current = window.setInterval(() => {
      // Always count — keeps both players in sync even when tab is hidden.
      // visibilitychange handler resyncs the display on tab return.
      cpuTimeRef.current = Math.max(0, cpuTimeRef.current - 1);
      setTimeRemaining(cpuTimeRef.current);

      if (cpuTimeRef.current <= 5 && cpuTimeRef.current > 0) {
        setMusicVolume(0.9);
        if (!momentumActive && lastHeartbeat.current !== cpuTimeRef.current) {
          playSfx('heartbeat_tick', 0.4);
          lastHeartbeat.current = cpuTimeRef.current;
        }
      } else if (cpuTimeRef.current > 5) {
        restoreMusicVolume();
        lastHeartbeat.current = null;
      }

      if (cpuTimeRef.current <= 0) {
        clearInterval(cpuTimerRef.current!);
        cpuTimerRef.current = null;

        // Read FRESH state from the store — the closure captures a stale snapshot
        // of `player` from when the selection phase started. By the time the timer
        // hits 0, the player may have already clicked a character, so we MUST use
        // the latest store state to avoid auto-selecting the wrong character.
        const latestPlayer = useGameStore.getState().player;
        if (!latestPlayer.selectionLocked && latestPlayer.team.length > 0) {
          const avail = latestPlayer.team.filter((c) => c.id !== tiredPlayerIdRef.current);
          const pool = avail.length ? avail : latestPlayer.team;
          const preselected = latestPlayer.selectedCharacter;
          const preselectedIsTired = preselected?.id === tiredPlayerIdRef.current;
          const picked = preselected && !preselectedIsTired
            ? preselected
            : pool[Math.floor(Math.random() * pool.length)];
          if (!picked) return;
          if (!latestPlayer.selectedCharacter || preselectedIsTired) selectCharacter(picked);
          lockSelection();
          if (mode === 'pvp' && matchId) {
            if (useV2) void submitCharacterSelectionV2(matchId, picked.id);
            else void updatePlayerSelection(matchId, picked.id, true, playerId);
          }
        }
        if (!opponent.selectionLocked && mode === 'cpu') {
          applyCpuBoosts();
          const pick = generateCpuSelection();
          if (pick) lockOpponentSelection(pick);
        }
      }
    }, 1000);

    return () => {
      if (cpuTimerRef.current) { clearInterval(cpuTimerRef.current); cpuTimerRef.current = null; }
      restoreMusicVolume();
      lastHeartbeat.current = null;
    };
  }, [phase, currentBattleRound]); // only restart on phase/round change

  // ─── Track opponent boost changes ────────────────────────────────────────
  const prevOpponentBoostsRef = useRef(opponentBoosts);
  useEffect(() => {
    const prev = prevOpponentBoostsRef.current;
    const notifications: string[] = [];
    let alert: { icon: LucideIcon; label: string; sub: string } | null = null;

    if (!prev.rerollUsed && opponentBoosts.rerollUsed) notifications.push(`${opponent.name} used Stat Reroll!`);
    if (!prev.extraTimeUsed && opponentBoosts.extraTimeUsed) notifications.push(`${opponent.name} used Extra Time!`);
    if (!prev.doubleWinUsed && opponentBoosts.doubleWinUsed) {
      notifications.push(`${opponent.name} activated Double Win!`);
      alert = { icon: ChevronsUp, label: 'DOUBLE WIN ARMED!', sub: `${opponent.name} wins this round = +2 points` };
    }
    if (!prev.statBoostUsed && opponentBoosts.statBoostUsed) {
      notifications.push(`${opponent.name} used Stat Boost +${opponentBoosts.statBoostPct}%!`);
      alert = { icon: TrendingUp, label: `OPPONENT BOOSTED +${opponentBoosts.statBoostPct}%`, sub: `${opponent.name}'s stat this round is stronger` };
    }
    if (!prev.weakenUsed && opponentBoosts.weakenUsed) {
      notifications.push(`${opponent.name} used Weaken -${opponentBoosts.weakenPct}%!`);
      alert = { icon: TrendingDown, label: `YOUR STAT WEAKENED -${opponentBoosts.weakenPct}%`, sub: `${opponent.name} debuffed you this round` };
    }
    if (notifications.length > 0) {
      setOpponentBoostBanner(notifications.join(' '));
      if (alert) setOpponentBoostAlert(alert);
      playSfx('card_flip');
    }
    prevOpponentBoostsRef.current = opponentBoosts;
  }, [opponentBoosts, opponent.name]);

  useEffect(() => {
    if (!boostBanner) return;
    const t = window.setTimeout(() => setBoostBanner(null), 1800);
    return () => window.clearTimeout(t);
  }, [boostBanner]);

  useEffect(() => {
    if (!opponentBoostBanner) return;
    const t = window.setTimeout(() => setOpponentBoostBanner(null), 2500);
    return () => window.clearTimeout(t);
  }, [opponentBoostBanner]);

  useEffect(() => {
    if (!opponentBoostAlert) return;
    const t = window.setTimeout(() => setOpponentBoostAlert(null), 3500);
    return () => window.clearTimeout(t);
  }, [opponentBoostAlert]);

  // ─── QUICK MESSAGES ─────────────────────────────────────────────────────
  useEffect(() => {
    if (mode !== 'pvp' || !matchId || !playerRole) return;
    const supabase = getSupabaseClient();
    if (!supabase) return;

    const channel = supabase
      .channel(`quick_msg_battle:${matchId}`)
      .on('broadcast', { event: 'quick_message' }, (payload) => {
        const p = payload.payload as { player_role: string; message: string };
        if (p.player_role === playerRole) return;
        playSfx('card_flip', 0.5);
        setIncomingMessage(p.message);
        if (incomingTimerRef.current) window.clearTimeout(incomingTimerRef.current);
        incomingTimerRef.current = window.setTimeout(() => setIncomingMessage(null), 3000);
      })
      .subscribe();

    quickMsgChannelRef.current = channel as unknown as { send: (msg: object) => Promise<unknown> };

    return () => {
      supabase.removeChannel(channel);
      quickMsgChannelRef.current = null;
    };
  }, [mode, matchId, playerRole]);

  const handleSendMessage = useCallback(async (text: string) => {
    if (!matchId || !playerRole || messageCooldown) return;
    setMessageCooldown(true);
    setSentMessage(text);
    if (quickMsgChannelRef.current) {
      await quickMsgChannelRef.current.send({
        type: 'broadcast',
        event: 'quick_message',
        payload: { player_role: playerRole, message: text, timestamp: new Date().toISOString() },
      });
    }
    if (messageCooldownTimerRef.current) window.clearTimeout(messageCooldownTimerRef.current);
    messageCooldownTimerRef.current = window.setTimeout(() => {
      setMessageCooldown(false);
      setSentMessage(null);
    }, 3000);
  }, [matchId, playerRole, messageCooldown]);

  // ─── PvP SUBSCRIPTIONS ───────────────────────────────────────────────────
  useEffect(() => {
    if (mode !== 'pvp' || !matchId) return;

    const unsubMatch = subscribeToMatch(matchId, (match) => {
      applyMatchSnapshot(match);
      if (match.status === 'finished') {
        const st = useGameStore.getState();
        // Guest derives result from match winner field when match finishes
        if (!isHost && st.resultOverride === null && match.winner) {
          const guestIsHost = false; // guest is never host
          setResultOverride(
            match.winner === 'draw' ? 'draw'
            : match.winner === 'host' ? 'opponent'  // host won = guest (me) lost
            : 'player'                               // guest won
          );
        }
        // Give guest time to see the final round result before navigating away
        if (st.phase !== 'results') {
          const delay = phaseRef.current === 'result' ? RESULT_AUTOADVANCE_MS + 500 : 0;
          window.setTimeout(() => setPhase('results'), delay);
        }
      }
    });

    const unsubPlayers = subscribeToPlayers(matchId, (players) => {
      applyPlayersSnapshot(players);
      if (isHost && players.length === 2 && playersCountRef.current < 2) toast.info(`${opponent.name} joined`);
      const prevCount = playersCountRef.current;
      playersCountRef.current = players.length;
      if (prevCount >= 2 && players.length < 2 && !opponentLeftRef.current) {
        const currentPhase = useGameStore.getState().phase;
        if (currentPhase === 'battle' || currentPhase === 'auction') {
          opponentLeftRef.current = true;
          setResultOverride('player');
          setPhase('results');
          toast.success('YOU WIN. Opponent forfeited.');
        }
      }
      // NOTE: Guest resultOverride is set ONLY by the host via resolveBattleRoundV2
      // (match_over flag) or by match status 'finished'. Do NOT derive it from
      // roundsWon here — that data can be stale and causes wrong scorecard.
    });

    // Server-authoritative battle history — both clients subscribe to match_rounds.
    // This guarantees identical battleHistory, tired character IDs, and scorecards.
    const unsubRounds = subscribeToBattleRounds(matchId, (rounds) => {
      applyBattleHistorySnapshot(rounds);
    });

    return () => { unsubMatch(); unsubPlayers(); unsubRounds(); };
  }, [mode, matchId]);

  // ─── BOTH SELECTED → COMPARISON ──────────────────────────────────────────
  useEffect(() => {
    if (
      phase === 'selection' &&
      player.selectionLocked && opponent.selectionLocked &&
      // Guard: require actual character selections — prevents stale locked state
      // (e.g. from a previous round that wasn't fully cleared) from triggering
      // an immediate transition at the start of the next selection phase.
      player.selectedCharacter && opponent.selectedCharacter
    ) {
      if (cpuTimerRef.current) { clearInterval(cpuTimerRef.current); cpuTimerRef.current = null; }
      setBattlePhase('comparison');
    }
  }, [player.selectionLocked, opponent.selectionLocked, phase, player.selectedCharacter, opponent.selectedCharacter]);

  // ─── COMPARISON → RESULT ─────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'comparison') return;
    // Snapshot how many rounds exist NOW — the guest uses this to wait until
    // subscribeToBattleRounds delivers the new round before showing the result card.
    battleHistoryLenAtComparisonRef.current = useGameStore.getState().battleHistory.length;
    duckMusic(0.1, 900);
    playSfx('card_flip');
    const t = window.setTimeout(() => {
      if (mode === 'cpu') {
        resolveBattle();
        setBattlePhase('result');
      } else if (isHost && !isResolving.current) {
        isResolving.current = true;
        void resolveBattleRemote();
      } else if (!isHost) {
        // Guest: don't resolve locally — subscribeToBattleRounds updates battleHistory
        // and the host's result writes to match_rounds which both clients subscribe to.
        // Set tired IDs immediately from the characters that just battled so the next
        // selection phase greys them out without waiting for the rounds subscription.
        const st = useGameStore.getState();
        if (st.player.selectedCharacter || st.opponent.selectedCharacter) {
          useGameStore.setState({
            tiredPlayerId: st.player.selectedCharacter?.id ?? null,
            tiredOpponentId: st.opponent.selectedCharacter?.id ?? null,
          });
        }
        setBattlePhase('result');
      }
    }, 1800);
    return () => window.clearTimeout(t);
  }, [phase]);

  // ─── RESULT HOLD ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'result' || !lastRound) return;
    setResultHold(true);
    if (lastRound.winner === 'player') playSfx('round_win');
    else if (lastRound.winner === 'opponent') playSfx('round_lose');
    const t = window.setTimeout(() => setResultHold(false), RESULT_HOLD_MS);
    return () => window.clearTimeout(t);
  }, [phase, lastRound?.stat, lastRound?.winner]);

  // ─── PvP AUTO-ADVANCE: result → stat-reveal ──────────────────────────────
  useEffect(() => {
    if (phase !== 'result' || mode !== 'pvp') return;
    const t = window.setTimeout(() => {
      isResolving.current = false;
      // Clear ALL stale selection state for both host and guest so the
      // "BOTH SELECTED" effect doesn't re-trigger at the start of the next
      // selection phase. This is the belt-and-suspenders path — the primary
      // clear is in resolveBattle(serverWins) and applyBattleHistorySnapshot.
      useGameStore.setState((s) => ({
        lastLocalSelectionLockAt: null,
        player: {
          ...s.player,
          selectedCharacter: null,
          selectionLocked: false,
        },
        opponent: {
          ...s.opponent,
          selectedCharacter: null,
          selectionLocked: false,
        },
      }));
      setBattlePhase('stat-reveal');
    }, RESULT_AUTOADVANCE_MS);
    return () => window.clearTimeout(t);
  }, [phase, mode]);

  // ─── SERVER RESOLVE (host only) ──────────────────────────────────────────
  const resolveBattleRemote = async () => {
    if (!matchId || !currentStat) return;

    const st = useGameStore.getState();
    const playerChar = st.player.selectedCharacter;
    const opponentChar = st.opponent.selectedCharacter;
    if (!playerChar || !opponentChar) { isResolving.current = false; return; }

    const pv = Math.round(
      getCharacterStatValue(playerChar, currentStat)
      * (1 + st.playerBoosts.statBoostPct / 100)
      * (1 - st.opponentBoosts.weakenPct / 100)
    );
    const ov = Math.round(
      getCharacterStatValue(opponentChar, currentStat)
      * (1 + st.opponentBoosts.statBoostPct / 100)
      * (1 - st.playerBoosts.weakenPct / 100)
    );
    const hv = playerRole === 'host' ? pv : ov;
    const gv = playerRole === 'host' ? ov : pv;

    const nextStat = getNextBattleStat({
      verse: settings.verse,
      usedStats: [...battleHistory.map((round) => round.stat), currentStat],
      currentStat,
      seed: currentBattleRound + 1,
    });
    const attempt = async (version: number) => resolveBattleRoundV2(matchId, hv, gv, version, nextStat);
    let result = await attempt(stateVersion);
    if (result.needsRetry && result.currentVersion !== undefined) {
      setStateVersion(result.currentVersion);
      result = await attempt(result.currentVersion);
    }

    if (result.success) {
      setStateVersion(result.data!.new_state_version);
      if (!result.data!.match_over && result.data!.next_stat) {
        useGameStore.getState().setCurrentStat(result.data!.next_stat);
      }

      const hostWins = result.data!.host_wins;
      const guestWins = result.data!.guest_wins;
      const serverWins =
        playerRole === 'host'
          ? { playerWins: hostWins, opponentWins: guestWins }
          : { playerWins: guestWins, opponentWins: hostWins };

      // Only update roundsWon — subscribeToBattleRounds rebuilds battleHistory
      resolveBattle(serverWins);
      setBattlePhase('result');

      if (result.data!.match_over) {
        const winner = result.data!.match_winner;
        setResultOverride(
          winner === null ? 'draw'
          : (winner === 'host') === (playerRole === 'host') ? 'player'
          : 'opponent'
        );
        // Delay so players see the final round result before results screen
        window.setTimeout(() => setPhase('results'), RESULT_AUTOADVANCE_MS + 500);
      }
    } else {
      resolveBattle();
      setBattlePhase('result');
      console.warn('Battle round sync failed:', result.error);
    }
    isResolving.current = false;
  };

  // ─── MOMENTUM SFX ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!momentumActive) { lastCrowdRef.current = null; return; }
    const key = `${player.roundsWon}-${opponent.roundsWon}`;
    if (lastCrowdRef.current !== key) { playSfx('crowd_swell', 0.8); lastCrowdRef.current = key; }
  }, [momentumActive, player.roundsWon, opponent.roundsWon]);

  // ─── MUSIC ───────────────────────────────────────────────────────────────
  useEffect(() => { playMusic('background_music_battle'); return () => stopMusic(); }, []);
  const toggleMusic = useCallback(() => {
    const nextMuted = !musicMuted;
    setMusicMuted(nextMuted);
    setMusicMutedState(nextMuted);
  }, [musicMuted]);

  useEffect(() => {
    return () => {
      if (cpuTimerRef.current) clearInterval(cpuTimerRef.current);
      if (messageCooldownTimerRef.current) window.clearTimeout(messageCooldownTimerRef.current);
      if (incomingTimerRef.current) window.clearTimeout(incomingTimerRef.current);
      restoreMusicVolume();
      stopMusic();
    };
  }, []);

  // ─── SELECT / LOCK ────────────────────────────────────────────────────────
  const handleSelectCharacter = (character: typeof player.team[0]) => {
    if (player.selectionLocked || character.id === effectiveTiredPlayerId) return;
    selectCharacter(character);
    if (mode === 'pvp' && matchId && !useV2) {
      void updatePlayerSelection(matchId, character.id, false, playerId);
    }
  };

  const handleLockSelection = async () => {
    if (!player.selectedCharacter) return;
    if (player.selectedCharacter.id === effectiveTiredPlayerId) {
      toast.error('That character is resting this round. Pick another!');
      return;
    }
    lockSelection();
    if (mode === 'pvp' && matchId) {
      if (useV2) {
        const result = await quickLockSelectionV2(matchId, player.selectedCharacter.id);
        if (!result.success) {
          if (result.error === 'character_is_tired') {
            // Server rejected — undo local lock and show error
            useGameStore.setState((s) => ({ player: { ...s.player, selectionLocked: false } }));
            toast.error('That character is resting this round. Pick another!');
            return;
          }
          await submitCharacterSelectionV2(matchId, player.selectedCharacter.id);
        }
      } else {
        void updatePlayerSelection(matchId, player.selectedCharacter.id, true, playerId);
      }
    }
    if (mode === 'cpu' && !opponent.selectionLocked) {
      const delay =
        settings.cpuDifficulty === 'hard'
          ? Math.random() * 600 + 200
          : Math.random() * 1500 + 500;
      window.setTimeout(() => {
        applyCpuBoosts();
        const p = generateCpuSelection();
        if (p) lockOpponentSelection(p);
      }, delay);
    }
  };

  const handleNextRound = () => {
    if (mode === 'pvp') {
      isResolving.current = false;
      setBattlePhase('stat-reveal');
    } else {
      setBattlePhase('stat-reveal');
      nextBattleRound();
    }
  };

  const handleLeaveGame = () => {
    if (mode === 'pvp' && matchId) void forfeitMatch(matchId);
    resetGame();
  };

  const lastResult = battleHistory[battleHistory.length - 1];
  const momentumOverlayClass = playerMatchPoint
    ? 'bg-[radial-gradient(circle_at_top,_rgba(0,160,255,0.22),_transparent_55%)]'
    : opponentMatchPoint
    ? 'bg-[radial-gradient(circle_at_top,_rgba(255,0,0,0.18),_transparent_55%)]'
    : '';

  if (!currentStat) {
    return (
      <div className="min-h-screen bg-arena flex items-center justify-center">
        <p className="text-lg text-muted-foreground">Waiting for battle stat...</p>
      </div>
    );
  }

  return (
    <div className="h-screen h-[100dvh] flex flex-col overflow-hidden bg-arena relative">
      {momentumActive && phase === 'selection' && (
        <div className={`pointer-events-none absolute inset-0 ${momentumOverlayClass}`} />
      )}

      {/* ── FULL-SCREEN OPPONENT BOOST ALERT ─────────────────────────────── */}
      <AnimatePresence>
        {opponentBoostAlert && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <motion.div className="absolute inset-0 bg-red-950/70"
              initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 0.6] }} transition={{ duration: 0.4 }} />
            <motion.div
              className="relative z-10 flex flex-col items-center gap-3 rounded-2xl border-2 border-red-500 bg-red-950/95 mx-4 px-8 py-7 shadow-2xl text-center"
              initial={{ scale: 0.5, opacity: 0, y: -30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', duration: 0.5 }}
            >
              <opponentBoostAlert.icon className="h-12 w-12 text-red-400" aria-hidden />
              <p className="text-xl font-display font-bold text-red-400 tracking-wide">{opponentBoostAlert.label}</p>
              <p className="text-sm text-red-300/80">{opponentBoostAlert.sub}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── HEADER ───────────────────────────────────────────────────────────
          Same pattern as AuctionScreen: absolute-centred scorecard so it's
          always truly centred regardless of how wide the side text is.
          min-h-[80px] guarantees the inset-y-0 centre has room to breathe. */}
      <header className="shrink-0 pt-12 pb-2 px-3 grid grid-cols-[1fr_auto_1fr] items-center gap-1">

        {/* YOUR side */}
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-player font-display font-bold text-sm leading-none">YOU</span>
          <span className="font-mono text-[11px] text-primary leading-none">{player.coins.toLocaleString()} coins</span>
          <span className={`text-[10px] font-semibold uppercase tracking-wide leading-none ${player.selectionLocked ? 'text-success' : 'text-muted-foreground'}`}>
            {player.selectionLocked ? '✓ Locked' : 'Not Locked'}
          </span>
        </div>

        {/* CENTRE scorecard — absolute so it never competes with side columns */}
        <div className="flex flex-col items-center text-center select-none shrink-0 px-2">
          <div className="flex items-baseline gap-2">
            <span className="font-mono font-black text-3xl text-player">{player.roundsWon}</span>
            <span className="font-mono font-black text-3xl text-muted-foreground">:</span>
            <span className="font-mono font-black text-3xl text-opponent">{opponent.roundsWon}</span>
          </div>
          <span className="text-[10px] text-muted-foreground leading-none">Rd {currentBattleRound} · First to {settings.battleFormat}</span>
          <button
            onClick={handleLeaveGame}
            className="text-[10px] text-muted-foreground/60 hover:text-muted-foreground mt-0.5 touch-manipulation leading-none"
          >
            Leave
          </button>
          <button
            onClick={toggleMusic}
            aria-label={musicMuted ? 'Turn site audio on' : 'Turn site audio off'}
            title={musicMuted ? 'Turn site audio on' : 'Turn site audio off'}
            className="mt-1 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground/70 hover:bg-secondary/60 hover:text-foreground touch-manipulation"
          >
            {musicMuted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
          </button>
        </div>

        {/* OPPONENT side */}
        <div className="flex flex-col gap-0.5 items-end min-w-0">
          <span className="text-opponent font-display font-bold text-sm leading-none truncate max-w-full text-right">{opponent.name}</span>
          <span className="font-mono text-[11px] text-opponent/70 leading-none">{opponent.coins.toLocaleString()} coins</span>
          <span className={`text-[10px] font-semibold uppercase tracking-wide leading-none ${opponent.selectionLocked ? 'text-success' : 'text-muted-foreground'}`}>
            {opponent.selectionLocked ? '✓ Locked' : 'Waiting...'}
          </span>
        </div>

      </header>

      {/* ── INCOMING MESSAGE OVERLAY ──────────────────────────────────────── */}
      <div style={{ position: 'fixed', top: 12, left: 0, right: 0, display: 'flex', justifyContent: 'center', zIndex: 9999, pointerEvents: 'none' }}>
        <AnimatePresence>
          {incomingMessage && (
            <motion.div
              key={incomingMessage}
              initial={{ opacity: 0, y: -10, scale: 0.88 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.92 }}
              transition={{ type: 'spring', stiffness: 400, damping: 24 }}
              className="rounded-2xl border-2 border-opponent/70 bg-background/95 backdrop-blur-sm px-4 py-2 text-sm font-display font-bold text-opponent shadow-2xl whitespace-nowrap"
            >
              {opponent.name}: {incomingMessage}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── SENT MESSAGE OVERLAY ──────────────────────────────────────────── */}
      <div style={{ position: 'fixed', top: 58, left: 0, right: 0, display: 'flex', justifyContent: 'center', zIndex: 9998, pointerEvents: 'none' }}>
        <AnimatePresence>
          {sentMessage && (
            <motion.div
              key={sentMessage}
              initial={{ opacity: 0, y: -10, scale: 0.88 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.92 }}
              transition={{ type: 'spring', stiffness: 400, damping: 24 }}
              className="rounded-2xl border-2 border-player/60 bg-background/90 backdrop-blur-sm px-4 py-2 text-sm font-display font-bold text-player shadow-xl whitespace-nowrap"
            >
              You: {sentMessage}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── OPPONENT BOOST BANNER ─────────────────────────────────────────── */}
      <AnimatePresence>
        {opponentBoostBanner && (
          <motion.div
            className="mx-3 mb-1 text-center text-xs font-display px-3 py-1.5 rounded-lg bg-opponent/10 border border-opponent/30 text-opponent"
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
          >
            {opponentBoostBanner}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── QUICK MESSAGE CHIPS (PvP only) ──────────────────────────────────── */}
      {mode === 'pvp' && (
        <div className="shrink-0 flex gap-1.5 overflow-x-auto px-3 py-2 border-b border-border/20 bg-background/30 scrollbar-none">
          {QUICK_MESSAGES.map(({ label, text }) => {
            const sent = sentMessage === text;
            return (
              <button
                key={text}
                onClick={() => void handleSendMessage(text)}
                disabled={messageCooldown}
                className={[
                  'shrink-0 rounded-full px-2.5 py-1 text-[10px] font-display font-semibold border transition-all leading-none select-none touch-manipulation',
                  sent
                    ? 'border-player/60 bg-player/20 text-player'
                    : messageCooldown
                      ? 'border-border/30 text-muted-foreground/40 cursor-not-allowed opacity-50'
                      : 'border-border/50 bg-secondary/40 text-muted-foreground active:scale-95 active:bg-player/10 active:text-player',
                ].join(' ')}
              >
                {sent ? `✓ ${label}` : label}
              </button>
            );
          })}
        </div>
      )}

      {/* ── MAIN ──────────────────────────────────────────────────────────── */}
      <main className="flex-1 overflow-y-auto">
        <div className="flex flex-col items-center justify-center min-h-full px-3 pb-24">
        <AnimatePresence mode="wait">

          {/* STAT REVEAL */}
          {phase === 'stat-reveal' && (() => {
            // In PvP: if the stat hasn't changed from the previous round yet,
            // show a brief loading state instead of the stale old stat.
            // CPU mode always has the new stat synchronously so skip the guard.
            const waitingForNewStat =
              mode === 'pvp' &&
              resultStatRef.current !== null &&
              currentStat === resultStatRef.current;
            if (waitingForNewStat) {
              return (
                <motion.div key="stat-reveal-loading" className="flex flex-col items-center gap-6 text-center"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <p className="text-xl text-muted-foreground font-display animate-pulse">Preparing next round...</p>
                </motion.div>
              );
            }
            return (
            <motion.div key="stat-reveal" className="flex flex-col items-center gap-6 text-center"
              initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
              <motion.p className="text-xl text-muted-foreground font-display" initial={{ y: -20 }} animate={{ y: 0 }}>
                This round's stat...
              </motion.p>
              <motion.h2
                className="text-4xl font-display font-bold text-primary"
                initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5 }}>
                {settings.hiddenStat ? 'HIDDEN STAT' : STAT_LABELS[currentStat].label.toUpperCase()}
              </motion.h2>
            </motion.div>
            );
          })()}

          {/* SELECTION */}
          {phase === 'selection' && (
            <motion.div key="selection" className="flex flex-col items-center gap-4 w-full"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>

              {!settings.hiddenStat && <StatBadge stat={currentStat} size="md" highlight />}

              <TimerDisplay seconds={timeRemaining} maxSeconds={15} />

              <p className="text-xs text-muted-foreground text-center">
                Choose your fighter and lock in before time runs out.
              </p>

              {boostBanner && (
                <motion.div className="text-center text-sm text-primary font-display"
                  initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}>
                  {boostBanner}
                </motion.div>
              )}

              {/* Character grid — sm portraits (96px) fit 3 across on mobile */}
              <div className="w-full">
                <p className="text-center text-xs text-muted-foreground mb-2">Select your champion</p>
                <div className="flex justify-center gap-2 flex-wrap">
                  {player.team.map((character) => {
                    const tired = character.id === effectiveTiredPlayerId;
                    return (
                      // The outer div is the click target blocker when tired.
                      // pointer-events-none on the whole tile prevents ANY tap/click
                      // from reaching the portrait or overlay beneath.
                      <div
                        key={character.id}
                        className={`relative ${tired ? 'pointer-events-none select-none' : ''}`}
                      >
                        <CharacterPortrait
                          character={character}
                          size="sm"
                          selected={player.selectedCharacter?.id === character.id}
                          onClick={tired ? undefined : () => handleSelectCharacter(character)}
                          showName
                          dimmed={tired}
                        />
                        {tired && (
                          <div className="absolute inset-0 rounded-xl bg-background/60 flex items-end justify-center pb-1">
                            <span className="text-[10px] font-display font-bold text-muted-foreground bg-background/90 rounded px-1">Resting</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Selected stat preview */}
              {player.selectedCharacter && !settings.hiddenStat && (
                <motion.div className="text-center" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                  <p className="text-xs text-muted-foreground">
                    {player.selectedCharacter.name}'s {STAT_LABELS[currentStat].label}:
                  </p>
                  <p className="font-mono font-bold text-2xl text-primary">
                    {getCharacterStatValue(player.selectedCharacter, currentStat).toLocaleString()}
                  </p>
                </motion.div>
              )}
              {player.selectedCharacter && settings.hiddenStat && (
                <p className="text-xs text-muted-foreground">Stat reveals after both lock in.</p>
              )}

              {/* Lock button */}
              <Button
                size="lg"
                onClick={handleLockSelection}
                disabled={!player.selectedCharacter || player.selectionLocked}
                className="w-full max-w-sm h-14 text-lg font-display font-bold bg-primary hover:bg-primary/90 touch-manipulation active:scale-[0.98]"
              >
                {player.selectionLocked ? '✓ Selection Locked' : 'Commit Selection'}
              </Button>

              {/* Lock status */}
              <div className="flex gap-6 text-xs justify-center">
                <span className={player.selectionLocked ? 'text-success font-semibold' : 'text-muted-foreground'}>
                  {player.selectionLocked ? '✓ You locked in' : '○ You'}
                </span>
                <span className={opponent.selectionLocked ? 'text-success font-semibold' : 'text-muted-foreground'}>
                  {opponent.selectionLocked ? `✓ ${opponent.name}` : `○ ${opponent.name}...`}
                </span>
              </div>
            </motion.div>
          )}

          {/* COMPARISON */}
          {phase === 'comparison' && player.selectedCharacter && opponent.selectedCharacter && (
            <motion.div key="comparison" className="flex flex-col items-center gap-5 w-full"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <StatBadge stat={currentStat} size="md" highlight />
              {/* md portraits (144px) side by side + gap-4 = 304px, fits 375px screen */}
              <div className="flex items-center gap-4 justify-center w-full">
                <motion.div className="flex flex-col items-center gap-2"
                  initial={{ x: -80, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.5 }}>
                  <CharacterPortrait character={player.selectedCharacter} size="md" showName />
                  <motion.div
                    className="font-mono font-bold text-2xl text-player"
                    initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.8, type: 'spring' }}>
                    {getDisplayStat(player.selectedCharacter, playerBoosts.statBoostPct, opponentBoosts.weakenPct).toLocaleString()}
                  </motion.div>
                </motion.div>
                <VsBadge />
                <motion.div className="flex flex-col items-center gap-2"
                  initial={{ x: 80, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.5 }}>
                  <CharacterPortrait character={opponent.selectedCharacter} size="md" showName />
                  <motion.div
                    className="font-mono font-bold text-2xl text-opponent"
                    initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.8, type: 'spring' }}>
                    {getDisplayStat(opponent.selectedCharacter, opponentBoosts.statBoostPct, playerBoosts.weakenPct).toLocaleString()}
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          )}

          {/* RESULT */}
          {phase === 'result' && lastResult && (() => {
            // In PvP (guest), the result card must wait until subscribeToBattleRounds
            // delivers the new round. Until then, battleHistory still has the PREVIOUS
            // round as lastResult — showing it would display wrong characters and winner.
            // The host resolves immediately and already has the correct lastResult.
            // CPU mode always has it synchronously.
            const waitingForServerResult =
              mode === 'pvp' &&
              battleHistory.length < currentBattleRound;

            if (waitingForServerResult) {
              return (
                <motion.div key="result-loading" className="flex flex-col items-center gap-4 text-center w-full"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <p className="text-lg text-muted-foreground animate-pulse font-display">Resolving round...</p>
                </motion.div>
              );
            }

            return (
            <motion.div key="result" className="flex flex-col items-center gap-4 text-center w-full"
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>

              <StatBadge stat={lastResult.stat} size="md" />

              {/* sm portraits (96px) side-by-side — compact on mobile */}
              <div className="flex items-center gap-6 justify-center">
                <div className="flex flex-col items-center gap-2">
                  {lastResult.playerCharacter && (
                    <CharacterPortrait
                      character={lastResult.playerCharacter} size="sm"
                      winner={lastResult.winner === 'player'} loser={lastResult.winner === 'opponent'} showName
                    />
                  )}
                  <div className={`font-mono font-bold text-xl ${lastResult.winner === 'player' ? 'text-success text-glow-success' : 'text-muted-foreground'}`}>
                    {lastResult.playerStatValue.toLocaleString()}
                  </div>
                </div>
                <div className="text-xl text-muted-foreground font-bold">vs</div>
                <div className="flex flex-col items-center gap-2">
                  {lastResult.opponentCharacter && (
                    <CharacterPortrait
                      character={lastResult.opponentCharacter} size="sm"
                      winner={lastResult.winner === 'opponent'} loser={lastResult.winner === 'player'} showName
                    />
                  )}
                  <div className={`font-mono font-bold text-xl ${lastResult.winner === 'opponent' ? 'text-opponent text-glow-opponent' : 'text-muted-foreground'}`}>
                    {lastResult.opponentStatValue.toLocaleString()}
                  </div>
                </div>
              </div>

              <motion.div initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }}>
                {lastResult.winner === 'player' && (
                  <p className="text-2xl font-display font-bold text-success text-glow-success">YOU WIN THIS ROUND!</p>
                )}
                {lastResult.winner === 'opponent' && (
                  <p className="text-2xl font-display font-bold text-opponent">OPPONENT WINS</p>
                )}
                {lastResult.winner === 'tie' && (
                  <p className="text-2xl font-display font-bold text-primary">TIE!</p>
                )}
              </motion.div>

              {/* Running score */}
              <div className="flex items-center gap-6 font-mono font-bold text-2xl">
                <span className="text-player">{player.roundsWon}</span>
                <span className="text-muted-foreground">:</span>
                <span className="text-opponent">{opponent.roundsWon}</span>
              </div>

              {/* Battle history scroll — shows all rounds so far */}
              {battleHistory.length > 1 && (
                <div className="w-full max-w-sm rounded-xl border border-border/50 bg-secondary/20 overflow-hidden">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground px-3 py-1.5 border-b border-border/40">All Rounds</p>
                  <div className="divide-y divide-border/30 max-h-40 overflow-y-auto">
                    {battleHistory.map((r, i) => (
                      <div key={i} className={`flex items-center gap-2 px-3 py-1.5 text-xs ${r === lastResult ? 'bg-primary/5' : ''}`}>
                        <span className="text-muted-foreground w-4 font-mono shrink-0">R{i + 1}</span>
                        <StatBadge stat={r.stat} size="sm" />
                        <span className={`flex-1 text-right truncate ${r.winner === 'player' ? 'text-success' : r.winner === 'opponent' ? 'text-opponent' : 'text-muted-foreground'}`}>
                          {r.playerCharacter?.name} <span className="text-muted-foreground/60">{r.playerStatValue} - {r.opponentStatValue}</span> {r.opponentCharacter?.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Advance controls */}
              {mode === 'cpu' ? (
                <Button
                  size="lg"
                  onClick={handleNextRound}
                  disabled={resultHold}
                  className="w-full max-w-sm h-14 text-lg font-display font-bold touch-manipulation"
                >
                  {resultHold ? 'Showing result...' : 'Next Round →'}
                </Button>
              ) : (
                <p className="text-sm text-muted-foreground animate-pulse">Next round in a moment...</p>
              )}
            </motion.div>
          );
          })()}

        </AnimatePresence>
        </div>
      </main>

      {/* ── BATTLE SHOP ────────────────────────────────────────────────────
          pb-20 on main gives clearance so content doesn't hide behind the shop button.
          Shop modal opens upward from the button. On mobile, constrains to viewport width. */}
      {(mode === 'cpu' || mode === 'pvp') && phase === 'selection' && (
        <div className="fixed bottom-safe bottom-4 right-4 z-50 flex flex-col items-end gap-2">
          {shopOpen && (
            <motion.div
              className="rounded-xl border border-border bg-secondary p-4 shadow-2xl w-[min(18rem,calc(100vw-2rem))]"
              initial={{ opacity: 0, y: 10, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-display font-bold">Battle Shop</p>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-muted-foreground font-mono">{player.coins.toLocaleString()} coins</p>
                  <button
                    onClick={() => setShopOpen(false)}
                    className="text-muted-foreground hover:text-foreground text-base leading-none touch-manipulation w-7 h-7 flex items-center justify-center"
                  >✕</button>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Button size="sm" variant="outline"
                  disabled={playerBoosts.rerollUsed || player.coins < 800}
                  className="justify-between touch-manipulation"
                  onClick={() => {
                    if (mode === 'cpu') {
                      if (purchaseReroll()) {
                        // Reset timer so players have full time on the new stat.
                        // Also unlock selection so the player can pick again for the new stat.
                        cpuTimeRef.current = 15;
                        setTimeRemaining(15);
                        useGameStore.setState((s) => ({
                          player: { ...s.player, selectionLocked: false, selectedCharacter: null },
                        }));
                        setBoostBanner('Stat rerolled. Pick again!');
                        setShopOpen(false);
                      }
                      return;
                    }
                    if (matchId && currentStat) {
                      const ns = getRandomStat({ verse: settings.verse, exclude: currentStat });
                      void purchaseBoost(matchId, 'reroll', undefined, ns);
                      // Unlock player selection so they can pick a character for the new stat.
                      // If they already locked in, un-lock them server-side too.
                      const wasLocked = useGameStore.getState().player.selectionLocked;
                      useGameStore.setState((s) => ({
                        playerBoosts: { ...s.playerBoosts, rerollEverUsed: true },
                        player: {
                          ...s.player,
                          selectionLocked: false,
                          selectedCharacter: null,
                        },
                        lastLocalSelectionLockAt: null,
                      }));
                      if (wasLocked) {
                        // Tell server to clear the lock so both clients see the player as unlocked.
                        void updatePlayerSelection(matchId, null, false, playerId);
                      }
                      // Reset timer so both players have full time on the new stat
                      cpuTimeRef.current = 15;
                      setTimeRemaining(15);
                      // Restart the selection-phase timer interval cleanly
                      if (cpuTimerRef.current) {
                        clearInterval(cpuTimerRef.current);
                        cpuTimerRef.current = null;
                      }
                      selectionStartedAtRef.current = Date.now();
                      cpuTimerRef.current = window.setInterval(() => {
                        cpuTimeRef.current = Math.max(0, cpuTimeRef.current - 1);
                        setTimeRemaining(cpuTimeRef.current);
                        if (cpuTimeRef.current <= 5 && cpuTimeRef.current > 0) {
                          setMusicVolume(0.9);
                          if (!momentumActive && lastHeartbeat.current !== cpuTimeRef.current) {
                            playSfx('heartbeat_tick', 0.4);
                            lastHeartbeat.current = cpuTimeRef.current;
                          }
                        } else if (cpuTimeRef.current > 5) {
                          restoreMusicVolume();
                          lastHeartbeat.current = null;
                        }
                        if (cpuTimeRef.current <= 0) {
                          clearInterval(cpuTimerRef.current!);
                          cpuTimerRef.current = null;
                          const latestPlayer = useGameStore.getState().player;
                          if (!latestPlayer.selectionLocked && latestPlayer.team.length > 0) {
                            const avail = latestPlayer.team.filter((c) => c.id !== tiredPlayerIdRef.current);
                            const pool = avail.length ? avail : latestPlayer.team;
                            const picked = pool[Math.floor(Math.random() * pool.length)];
                            if (!picked) return;
                            selectCharacter(picked);
                            lockSelection();
                            if (useV2) void submitCharacterSelectionV2(matchId, picked.id);
                            else void updatePlayerSelection(matchId, picked.id, true, playerId);
                          }
                        }
                      }, 1000);
                      setBoostBanner('Stat rerolled. Pick again!');
                      setShopOpen(false);
                    }
                  }}>
                  {playerBoosts.rerollUsed ? '✓ Reroll Used' : <><span>Reroll Stat</span><span className="font-mono">800</span></>}
                </Button>

                <Button size="sm" variant="outline"
                  disabled={playerBoosts.extraTimeUsed || player.coins < 750}
                  className="justify-between touch-manipulation"
                  onClick={() => {
                    if (mode === 'cpu') { if (purchaseExtraTime()) { setBoostBanner('+5s!'); setShopOpen(false); } return; }
                    if (matchId) {
                      void purchaseBoost(matchId, 'extra_time');
                      useGameStore.setState((s) => ({ playerBoosts: { ...s.playerBoosts, extraTimeEverUsed: true } }));
                      cpuTimeRef.current += 5;
                      setTimeRemaining(cpuTimeRef.current);
                      setBoostBanner('+5 seconds!'); setShopOpen(false);
                    }
                  }}>
                  {playerBoosts.extraTimeUsed ? '✓ Extra Time Used' : <><span>Extra Time +5s</span><span className="font-mono">750</span></>}
                </Button>

                <Button size="sm" variant="outline"
                  disabled={playerBoosts.doubleWinUsed || player.coins < 2000}
                  className="justify-between touch-manipulation"
                  onClick={() => {
                    if (mode === 'cpu') { if (purchaseDoubleWin()) { setBoostBanner('Double Win!'); setShopOpen(false); } return; }
                    if (matchId) { void purchaseBoost(matchId, 'double_win'); useGameStore.setState((s) => ({ playerBoosts: { ...s.playerBoosts, doubleWinEverUsed: true } })); setBoostBanner('Double Win armed!'); setShopOpen(false); }
                  }}>
                  {playerBoosts.doubleWinUsed ? '✓ Double Win Armed' : <><span>Double Win</span><span className="font-mono">2000</span></>}
                </Button>

                <div className="h-px bg-border/50 my-1" />
                <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wide">Stat Boost (self)</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button size="sm" variant="outline"
                    disabled={playerBoosts.statBoostUsed || player.coins < 500}
                    className="justify-between touch-manipulation"
                    onClick={() => {
                      if (mode === 'cpu') { if (purchaseStatBoost(5)) { setBoostBanner('+5%!'); setShopOpen(false); } return; }
                      if (matchId) { void purchaseBoost(matchId, 'stat_boost', 5); useGameStore.setState((s) => ({ playerBoosts: { ...s.playerBoosts, statBoostEverUsed: true, statBoostPctRecord: Math.max(s.playerBoosts.statBoostPctRecord, 5) } })); setBoostBanner('Stat +5%!'); setShopOpen(false); }
                    }}>
                    <span>+5%</span><span className="font-mono">500</span>
                  </Button>
                  <Button size="sm" variant="outline"
                    disabled={playerBoosts.statBoostUsed || player.coins < 1000}
                    className="justify-between touch-manipulation"
                    onClick={() => {
                      if (mode === 'cpu') { if (purchaseStatBoost(10)) { setBoostBanner('+10%!'); setShopOpen(false); } return; }
                      if (matchId) { void purchaseBoost(matchId, 'stat_boost', 10); useGameStore.setState((s) => ({ playerBoosts: { ...s.playerBoosts, statBoostEverUsed: true, statBoostPctRecord: Math.max(s.playerBoosts.statBoostPctRecord, 10) } })); setBoostBanner('Stat +10%!'); setShopOpen(false); }
                    }}>
                    <span>+10%</span><span className="font-mono">1000</span>
                  </Button>
                </div>

                <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wide mt-1">Weaken Opponent</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button size="sm" variant="outline"
                    disabled={playerBoosts.weakenUsed || player.coins < 600}
                    className="justify-between border-opponent/40 text-opponent hover:bg-opponent/10 touch-manipulation"
                    onClick={() => {
                      if (mode === 'cpu') { if (purchaseWeaken(5)) { setBoostBanner('Opp -5%!'); setShopOpen(false); } return; }
                      if (matchId) { void purchaseBoost(matchId, 'weaken', 5); useGameStore.setState((s) => ({ playerBoosts: { ...s.playerBoosts, weakenEverUsed: true, weakenPctRecord: Math.max(s.playerBoosts.weakenPctRecord, 5) } })); setBoostBanner('Opp weakened -5%!'); setShopOpen(false); }
                    }}>
                    <span>-5%</span><span className="font-mono">600</span>
                  </Button>
                  <Button size="sm" variant="outline"
                    disabled={playerBoosts.weakenUsed || player.coins < 1200}
                    className="justify-between border-opponent/40 text-opponent hover:bg-opponent/10 touch-manipulation"
                    onClick={() => {
                      if (mode === 'cpu') { if (purchaseWeaken(10)) { setBoostBanner('Opp -10%!'); setShopOpen(false); } return; }
                      if (matchId) { void purchaseBoost(matchId, 'weaken', 10); useGameStore.setState((s) => ({ playerBoosts: { ...s.playerBoosts, weakenEverUsed: true, weakenPctRecord: Math.max(s.playerBoosts.weakenPctRecord, 10) } })); setBoostBanner('Opp weakened -10%!'); setShopOpen(false); }
                    }}>
                    <span>-10%</span><span className="font-mono">1200</span>
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          <Button
            variant="outline"
            onClick={() => setShopOpen((p) => !p)}
            className={`rounded-full h-12 px-5 shadow-lg font-display touch-manipulation shadow-background/50 ${player.coins >= 500 ? 'border-primary text-primary' : 'border-muted text-muted-foreground'}`}
          >
            {shopOpen ? 'Close' : <><ShoppingBag className="mr-2 h-4 w-4" aria-hidden />Shop</>}
          </Button>
        </div>
      )}
    </div>
  );
}
